
import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, GraduationCap, TrendingUp, Award, Users, Code, ArrowRightLeft, Loader2, ChevronDown, ChevronUp, GitBranch, ThumbsDown, Undo2, Bookmark } from 'lucide-react';
import WhatIfNodeCarousel from './WhatIfNodeCarousel';
import './InteractiveRoadmapNode.css';

const InteractiveRoadmapNode = ({
  node,
  index,
  riskLevel,
  isLeft,
  isSelected,
  softColor,
  onSelect,
  onSwap,
  onDislike,
  onSaveRole,
  isSaved,
  userContext,
  roadmapId,
  isOverridden,
  isOverrideStart,
  originalNode,
  onResetOverride,
  onWhatIfClick,
  onApplyWithTsenta,
}) => {
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [alternatives, setAlternatives] = useState([]);
  const [loadingAlts, setLoadingAlts] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

  // Calculate year with fallback (2-year increments from current year)
  const displayYear = node.year || (new Date().getFullYear() + (index * 2));

  // Icon mapping
  const iconMap = {
    role: Briefcase,
    education: GraduationCap,
    skill: Code,
    achievement: Award,
    network: Users,
    milestone: TrendingUp
  };

  const Icon = iconMap[node.type] || Briefcase;

  // Helper to clean seniority from titles
  const getCleanTitle = (title) => title ? title.split(/ at /i)[0].trim() : '';

  const handleToggleAlternatives = async (e) => {
    e.stopPropagation(); // Prevent card selection
    
    if (showAlternatives) {
        setShowAlternatives(false);
        return;
    }

    setShowAlternatives(true);

    if (alternatives.length === 0) {
        setLoadingAlts(true);

        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000); // 20s timeout

        try {
            const { data: { session: _altSession } } = await supabase.auth.getSession();
            const response = await fetch(`${BACKEND_URL}/api/generate-alternatives`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${_altSession?.access_token}`,
                },
                body: JSON.stringify({
                    role: node,
                    user_context: userContext,
                    risk_level: riskLevel,
                    roadmap_id: roadmapId
                }),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                const data = await response.json();
                setAlternatives(data);
            } else {
                console.error("Failed to fetch alternatives");
            }
        } catch (error) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') {
                console.error("Alternatives request timed out");
            } else {
                console.error("Failed to load alternatives", error);
            }
        } finally {
            setLoadingAlts(false);
        }
    }
  };

  const handleSwap = (altRole) => {
    onSwap(index, altRole);
    setShowAlternatives(false);
  };

  return (
    <div
        className={`roadmap-node ${isLeft ? 'node-left' : 'node-right'}`}
        data-whatif-target={index}
        onClick={(e) => {
          // Mobile fallback: if onWhatIfClick exists and user taps, allow triggering what-if
        }}
    >
      <div className="node-spacer" />
      
      {/* Node Circle */}
      <div className="node-circle-container">
         <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.currentTarget.blur(); onSelect(node, index); }}
            className={`node-circle-button ${isSelected ? 'selected' : ''}`}
            style={{
                borderColor: softColor,
                backgroundColor: isSelected ? softColor : '#FFFFFF',
                boxShadow: isSelected ? `0 0 20px ${softColor}40` : '0 2px 4px rgba(0,0,0,0.1)'
            }}
         >
            <Icon className="node-icon" style={{ color: isSelected ? '#121212' : softColor }} />
         </button>
         <div className="node-year-badge" style={{ backgroundColor: softColor, color: '#121212', fontWeight: 700 }}>{displayYear}</div>
      </div>

      {/* Main Content Card */}
      <div className="node-content-wrapper" style={{ position: 'relative' }}>
          
          <motion.div
            className={`node-content-card ${isSelected ? 'expanded' : ''} cursor-pointer group hover:border-purple-300 hover:shadow-lg transition-all`}
            onClick={(e) => { e.preventDefault(); onSelect(node, index); }}
            onMouseDown={(e) => e.preventDefault()}
            style={{
                borderColor: isOverridden ? '#f59e0b' : (isSelected ? softColor : 'rgba(0,0,0,0.1)'),
                borderStyle: isOverridden ? 'dashed' : 'solid',
                borderWidth: isOverridden ? '2px' : undefined,
                boxShadow: isOverridden
                  ? '0 0 20px rgba(245, 158, 11, 0.15)'
                  : (isSelected ? `0 0 30px ${softColor}20` : '0 4px 12px rgba(0,0,0,0.05)'),
                position: 'relative',
                background: isOverridden ? '#FFFBEB' : '#FFFFFF',
                overflow: 'visible' // Allow alternatives to expand
            }}
          >
             {/* What-If Badge */}
             {isOverridden && (
               <div className="absolute -top-2 left-4 z-30">
                 <span className="px-2 py-0.5 bg-amber-400 text-amber-900 text-[9px] font-black uppercase tracking-wider rounded-full shadow-sm">
                   What-If
                 </span>
               </div>
             )}
             {/* Action Buttons */}
             <div className="absolute top-3 right-3 flex items-center gap-1 z-20">
                {/* Bookmark Button */}
                {onSaveRole && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSaveRole(node, e);
                    }}
                    className="p-2 rounded-full hover:bg-green-50 transition-colors group"
                    title={isSaved ? 'Remove bookmark' : 'Save to careers'}
                  >
                    <Bookmark
                      size={16}
                      className="transition-colors"
                      style={{ color: isSaved ? '#1ed760' : '#94a3b8' }}
                      fill={isSaved ? '#1ed760' : 'none'}
                    />
                  </button>
                )}
                {/* Dislike Button */}
                {onDislike && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDislike(node, riskLevel);
                    }}
                    className="p-2 rounded-full hover:bg-red-50 transition-colors group"
                    title="Replace this role"
                  >
                    <ThumbsDown size={16} className="text-slate-400 group-hover:text-red-500" />
                  </button>
                )}
                {/* Swap Button */}
                <button
                   type="button"
                   onClick={handleToggleAlternatives}
                   className="p-2 rounded-full hover:bg-slate-100 transition-colors group"
                   title="Swap Role"
                >
                   <motion.div
                       animate={{ rotate: showAlternatives ? 180 : 0 }}
                       transition={{ duration: 0.3 }}
                   >
                       <ArrowRightLeft size={16} className="text-slate-400 group-hover:text-purple-600" />
                   </motion.div>
                </button>
             </div>

             <h4 className="node-title group-hover:text-purple-600 transition-colors flex items-center gap-2" style={{ fontFamily: "'Montserrat', sans-serif", paddingRight: '6.5rem' }}>
                 {getCleanTitle(node.title)}
                 <ChevronDown size={14} className="opacity-0 group-hover:opacity-50 transition-opacity" />
             </h4>
             <p className="node-description text-sm text-slate-600 line-clamp-3 mb-2">{node.description}</p>
             
             {/* Salary & Details - Always Visible */}
             <div className="flex flex-col gap-1 mt-2 border-t border-slate-100 pt-2">
                 {(node.salary_range || node.salary) && (
                     <div className="node-salary-mini flex items-center gap-2">
                        <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wide">Est. Salary</span>
                        <span style={{color: softColor, fontWeight: 700, fontSize: '0.8rem'}}>
                            {node.salary
                                ? node.salary
                                : `${node.salary_range.min ? (node.salary_range.min/1000).toFixed(0) + 'k' : ''} - ${node.salary_range.max ? (node.salary_range.max/1000).toFixed(0) + 'k' : ''}`
                            }
                        </span>
                     </div>
                 )}
             </div>

             {/* Companies + Apply row */}
             {(node.companies?.length > 0 || onApplyWithTsenta) && (
               <div className="mt-3 pt-3 border-t border-slate-100 flex items-end justify-between gap-2">
                 {node.companies && node.companies.length > 0 ? (
                   <div className="flex-1 min-w-0">
                     <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wide mb-2">Where to find this role</p>
                     <div className="flex flex-wrap gap-1.5">
                       {node.companies.slice(0, 5).map((company, i) => (
                         <span
                           key={i}
                           className="text-xs font-semibold px-2 py-1 rounded-full"
                           style={{
                             backgroundColor: `${softColor}15`,
                             color: softColor,
                             border: `1px solid ${softColor}30`
                           }}
                         >
                           {company}
                         </span>
                       ))}
                     </div>
                   </div>
                 ) : <div className="flex-1" />}

                 {onApplyWithTsenta && (
                   <button
                     type="button"
                     onClick={(e) => { e.stopPropagation(); onApplyWithTsenta(node); }}
                     className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:opacity-90 active:scale-95"
                     style={{
                       background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                       color: '#fff',
                       border: 'none',
                       letterSpacing: '0.02em',
                     }}
                   >
                     <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                       <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                       <polyline points="17 8 12 3 7 8"/>
                       <line x1="12" y1="3" x2="12" y2="15"/>
                     </svg>
                     Apply
                   </button>
                 )}
               </div>
             )}

             {/* Reset Override Button */}
             {isOverrideStart && onResetOverride && (
               <div className="mt-3 pt-3 border-t border-amber-200">
                 <button
                   type="button"
                   onClick={(e) => {
                     e.stopPropagation();
                     onResetOverride();
                   }}
                   className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                 >
                   <Undo2 size={12} />
                   Revert to Original
                 </button>
               </div>
             )}

             {/* What-If Carousel (Original vs Modified comparison) */}
             {isOverridden && originalNode && (
               <WhatIfNodeCarousel
                 originalNode={originalNode}
                 currentNode={node}
                 softColor={softColor}
               />
             )}

             {/* Inline Alternatives Section */}
             <AnimatePresence>
                {showAlternatives && (
                    <motion.div
                        initial={{ height: 0, opacity: 0, marginTop: 0 }}
                        animate={{ height: 'auto', opacity: 1, marginTop: 16 }}
                        exit={{ height: 0, opacity: 0, marginTop: 0 }}
                        className="overflow-hidden w-full border-t border-slate-100"
                    >
                        <div className="pt-4 pb-2">
                            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                <GitBranch size={12} />
                                Alternative Paths
                            </div>

                            {loadingAlts ? (
                                <div className="flex flex-col items-center justify-center py-8 text-slate-400 gap-2">
                                    <Loader2 size={20} className="animate-spin" />
                                    <span className="text-xs">Finding matches...</span>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {alternatives.map((alt, i) => (
                                        <motion.div
                                            key={i}
                                            initial={{ x: -10, opacity: 0 }}
                                            animate={{ x: 0, opacity: 1 }}
                                            transition={{ delay: i * 0.1 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSwap(alt);
                                            }}
                                            className="p-3 rounded-lg border border-slate-200 bg-slate-50 hover:bg-white hover:border-purple-300 hover:shadow-md cursor-pointer transition-all group"
                                        >
                                            <div className="flex justify-between items-start mb-1">
                                                <h5 className="font-bold text-sm text-slate-700 group-hover:text-purple-700 transition-colors">
                                                    {getCleanTitle(alt.title)}
                                                </h5>
                                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-500 uppercase">
                                                    {alt.type || 'Pivot'}
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500 line-clamp-2 mb-2">{alt.description}</p>
                                            <div className="flex items-center justify-between text-[10px] text-slate-400">
                                                <span>{alt.salary}</span>
                                                <span className="flex items-center gap-1 group-hover:text-purple-600 font-medium">
                                                    Select <ArrowRightLeft size={10} />
                                                </span>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
             </AnimatePresence>

          </motion.div>
      </div>
    </div>
  );
};

export default InteractiveRoadmapNode;
